import React, { useState, useCallback } from 'react';
import {
  Grid, 
  FormControl, 
  Typography, 
  TextField, 
  Select, 
  MenuItem, 
  FormHelperText,
  Alert,
  Box,
  IconButton,
  InputAdornment
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { validateForm, validateFieldRealTime } from '../utils/formValidation';
import { usePasswordValidation } from '../hooks/usePasswordValidation';
import PasswordRequirements from './PasswordRequirements';
import { logError, logInfo } from '../utils/logger';

// Styled components for better UX
const StyledFormControl = styled(FormControl)(({ theme }) => ({
  width: '100%',
  marginBottom: theme.spacing(2),
}));

const RequiredLabel = styled('span')(({ theme }) => ({
  '&::after': {
    content: '" *"',
    color: theme.palette.error.main,
  },
}));

// Connection options for the dropdown
const CONNECTION_OPTIONS = [
  { value: 'Twitter', label: 'Twitter' },
  { value: 'Facebook', label: 'Facebook' },
  { value: 'Instagram', label: 'Instagram' },
  { value: 'Blue Sky', label: 'Blue Sky' },
  { value: 'TikTok', label: 'TikTok' },
  { value: 'LinkedIn', label: 'LinkedIn' },
  { value: 'Mastodon', label: 'Mastodon' },
  { value: 'Threads', label: 'Threads' },
  { value: 'Email', label: 'Email Newsletter' },
  { value: 'Friend', label: 'Friend/Referral' },
  { value: 'Search Engine', label: 'Search Engine (Google, etc.)' },
  { value: 'YouTube', label: 'YouTube' },
  { value: 'Podcast', label: 'Podcast' },
  { value: 'Blog/Website', label: 'Blog/Website' },
  { value: 'Other', label: 'Other' },
];

/**
 * OnboardingStep1 Component
 * First step of the user onboarding process with comprehensive form validation
 * Now includes password strength requirements and confirmation matching
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Function} props.setFormData - Function to update parent form data
 * @param {Object} props.formData - Current form data state
 * @param {Function} props.onValidationChange - Function to report validation status to parent
 */
export default function OnboardingStep1({ setFormData, formData = {}, onValidationChange }) {
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isValidating, setIsValidating] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Password validation hook
  const {
    requirements: passwordRequirements,
    strength: passwordStrength,
    isPasswordValid,
    getRequirementDescriptions,
    getFailedRequirements
  } = usePasswordValidation(
    formData.password || '',
    formData.confirm_password || ''
  );

  // Required fields for this step
  const requiredFields = ['first_name', 'last_name', 'password', 'confirm_password', 'connection'];

  /**
   * Validates the entire form and updates error state
   * @param {Object} currentFormData - Current form data to validate
   * @returns {boolean} True if form is valid
   */
  const validateFormStep = useCallback((currentFormData) => {
    setIsValidating(true);
    
    try {
      // Use existing validation for basic fields
      const basicValidationErrors = validateForm(currentFormData, requiredFields.filter(field => 
        !['password', 'confirm_password'].includes(field)
      ));
      
      // Add password-specific validation
      const allErrors = { ...basicValidationErrors };
      
      // Password validation using the hook
      if (!currentFormData.password || currentFormData.password.trim().length === 0) {
        allErrors.password = 'Password is required';
      } else if (!passwordRequirements.minLength) {
        allErrors.password = 'Password must be at least 8 characters long';
      } else if (!passwordRequirements.hasNumber || !passwordRequirements.hasUpper || 
                 !passwordRequirements.hasLower || !passwordRequirements.hasSpecial) {
        allErrors.password = 'Password does not meet strength requirements';
      }
      
      // Confirm password validation
      if (!currentFormData.confirm_password || currentFormData.confirm_password.trim().length === 0) {
        allErrors.confirm_password = 'Password confirmation is required';
      } else if (!passwordRequirements.passwordsMatch) {
        allErrors.confirm_password = 'Passwords do not match';
      }
      
      setErrors(allErrors);
      
      const isValid = Object.keys(allErrors).length === 0;
      
      // Report validation status to parent component
      if (onValidationChange) {
        onValidationChange(isValid, allErrors);
      }
      
      logInfo('Onboarding Step 1 validation completed', {
        isValid,
        errorCount: Object.keys(allErrors).length,
        fields: Object.keys(currentFormData),
        passwordStrength: passwordStrength.level,
        failedPasswordRequirements: getFailedRequirements()
      });
      
      return isValid;
    } catch (error) {
      logError('Onboarding Step 1 validation error', { error: error.message });
      setErrors({ _general: 'Validation error occurred. Please try again.' });
      return false;
    } finally {
      setIsValidating(false);
    }
  }, [requiredFields, onValidationChange, passwordRequirements, passwordStrength, getFailedRequirements]);

  /**
   * Handles form field changes
   * @param {Event} event - The change event
   */
  const handleChange = useCallback((event) => {
    const { name, value } = event.target;
    
    try {
      // Update form data
      setFormData(prevFormData => ({ 
        ...prevFormData, 
        [name]: value 
      }));

      // Mark field as touched
      setTouched(prev => ({ ...prev, [name]: true }));

      // Clear errors when user starts typing
      if (value.length > 0) {
        setErrors(prev => ({
          ...prev,
          [name]: '',
          _general: ''
        }));
      }
    } catch (error) {
      logError('Field change error', { fieldName: name, error: error.message });
    }
  }, [setFormData]);

  /**
   * Handles field blur events for validation
   * @param {Event} event - The blur event
   */
  const handleBlur = useCallback((event) => {
    const { name, value } = event.target;
    
    setTouched(prev => ({ ...prev, [name]: true }));
    
    // Simple validation on blur for non-password fields
    if (!['password', 'confirm_password'].includes(name)) {
      // Basic validation without complex dependencies
      let fieldError = '';
      
      if (name === 'first_name' || name === 'last_name') {
        if (!value || value.trim().length === 0) {
          fieldError = `${name === 'first_name' ? 'First' : 'Last'} name is required`;
        } else if (value.trim().length > 50) {
          fieldError = `${name === 'first_name' ? 'First' : 'Last'} name is too long`;
        }
      } else if (name === 'connection') {
        if (!value || value.trim().length === 0) {
          fieldError = 'Please select how you found us';
        }
      }
      
      setErrors(prev => ({
        ...prev,
        [name]: fieldError
      }));
    }
  }, []);

  /**
   * Gets the error message for a specific field
   * @param {string} fieldName - Field name
   * @returns {string} Error message or empty string
   */
  const getFieldError = useCallback((fieldName) => {
    return errors[fieldName] || '';
  }, [errors]);

  /**
   * Checks if a field has an error
   * @param {string} fieldName - Field name
   * @returns {boolean} True if field has error
   */
  const hasFieldError = useCallback((fieldName) => {
    return Boolean(errors[fieldName] && touched[fieldName]);
  }, [errors, touched]);

  /**
   * Toggles password visibility
   */
  const handleTogglePasswordVisibility = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);

  /**
   * Toggles confirm password visibility
   */
  const handleToggleConfirmPasswordVisibility = useCallback(() => {
    setShowConfirmPassword(prev => !prev);
  }, []);

  /**
   * Prevents mouse down event on password toggle button
   */
  const handleMouseDownPassword = useCallback((event) => {
    event.preventDefault();
  }, []);

  // Debounced validation for parent component
  React.useEffect(() => {
    if (Object.keys(formData).length > 0) {
      const timeoutId = setTimeout(() => {
        validateFormStep(formData);
      }, 300); // Debounce validation by 300ms

      return () => clearTimeout(timeoutId);
    }
  }, [formData, validateFormStep]);

  return (
    <Box sx={{ width: '100%', maxWidth: 600, mx: 'auto' }}>
      <Typography 
        variant="h4" 
        align="center" 
        gutterBottom 
        sx={{ mb: 2 }}
      >
        Complete your account setup!
      </Typography>
      <Typography 
        variant="body1" 
        align="center" 
        color="text.secondary"
        sx={{ mb: 4 }}
      >
        We've confirmed your account, now let's complete your profile
      </Typography>

      {/* General error alert */}
      {errors._general && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {errors._general}
        </Alert>
      )}

      <form noValidate>
        <Grid container spacing={2}>
          {/* First Name Field */}
          <Grid item xs={12} sm={6}>
            <StyledFormControl>
              <TextField
                required
                fullWidth
                id="first_name"
                name="first_name"
                label={<RequiredLabel>First Name</RequiredLabel>}
                value={formData.first_name || ''}
                onChange={handleChange}
                onBlur={handleBlur}
                error={hasFieldError('first_name')}
                helperText={getFieldError('first_name') || 'Enter your first name'}
                variant="outlined"
                disabled={isValidating}
                inputProps={{
                  'aria-describedby': 'first-name-helper',
                  maxLength: 50,
                }}
              />
            </StyledFormControl>
          </Grid>

          {/* Last Name Field */}
          <Grid item xs={12} sm={6}>
            <StyledFormControl>
              <TextField
                required
                fullWidth
                id="last_name"
                name="last_name"
                label={<RequiredLabel>Last Name</RequiredLabel>}
                value={formData.last_name || ''}
                onChange={handleChange}
                onBlur={handleBlur}
                error={hasFieldError('last_name')}
                helperText={getFieldError('last_name') || 'Enter your last name'}
                variant="outlined"
                disabled={isValidating}
                inputProps={{
                  'aria-describedby': 'last-name-helper',
                  maxLength: 50,
                }}
              />
            </StyledFormControl>
          </Grid>

          {/* Password Field */}
          <Grid item xs={12}>
            <StyledFormControl>
              <TextField
                required
                fullWidth
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                label={<RequiredLabel>Password</RequiredLabel>}
                value={formData.password || ''}
                onChange={handleChange}
                onBlur={handleBlur}
                error={hasFieldError('password')}
                helperText={getFieldError('password') || 'Create a strong password'}
                variant="outlined"
                disabled={isValidating}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleTogglePasswordVisibility}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        disabled={isValidating}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                inputProps={{
                  'aria-describedby': 'password-helper password-requirements',
                  maxLength: 128,
                  autoComplete: 'new-password'
                }}
              />
            </StyledFormControl>
          </Grid>

          {/* Password Requirements Display */}
          {(touched.password || (formData.password && formData.password.length > 0)) && (
            <Grid item xs={12}>
              <PasswordRequirements
                requirements={passwordRequirements}
                strength={passwordStrength}
                getRequirementDescriptions={getRequirementDescriptions}
                showStrength={true}
                compact={false}
                variant="outlined"
                id="password-requirements"
              />
            </Grid>
          )}

          {/* Confirm Password Field */}
          <Grid item xs={12}>
            <StyledFormControl>
              <TextField
                required
                fullWidth
                id="confirm_password"
                name="confirm_password"
                type={showConfirmPassword ? 'text' : 'password'}
                label={<RequiredLabel>Confirm Password</RequiredLabel>}
                value={formData.confirm_password || ''}
                onChange={handleChange}
                onBlur={handleBlur}
                error={hasFieldError('confirm_password')}
                helperText={
                  getFieldError('confirm_password') || 
                  'Re-enter your password to confirm'
                }
                variant="outlined"
                disabled={isValidating}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={handleToggleConfirmPasswordVisibility}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        disabled={isValidating}
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                inputProps={{
                  'aria-describedby': 'confirm-password-helper',
                  maxLength: 128,
                  autoComplete: 'new-password'
                }}
              />
            </StyledFormControl>
          </Grid>

          {/* How did you find us Field */}
          <Grid item xs={12}>
            <StyledFormControl>
              <TextField
                required
                fullWidth
                select
                id="connection"
                name="connection"
                label={<RequiredLabel>How did you find us?</RequiredLabel>}
                value={formData.connection || ''}
                onChange={handleChange}
                onBlur={handleBlur}
                error={hasFieldError('connection')}
                helperText={
                  getFieldError('connection') || 
                  'Help us understand how you discovered SoloSocial'
                }
                variant="outlined"
                disabled={isValidating}
                SelectProps={{
                  'aria-describedby': 'connection-helper',
                }}
              >
                <MenuItem value="" disabled>
                  <em>Please select an option</em>
                </MenuItem>
                {CONNECTION_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </StyledFormControl>
          </Grid>
        </Grid>
      </form>

    </Box>
  );
}
